import { onCleanup } from "solid-js";
import type { BetterFetch } from "@better-fetch/fetch";
import { createAuthClient } from "better-auth/solid";
import {
  magicLinkClient,
  organizationClient,
  anonymousClient,
  adminClient,
  inferOrgAdditionalFields,
} from "better-auth/client/plugins";
import { ac, roles } from "../../server/lib/organization-ac";
import type { auth } from "../../server/lib/auth";

/**
 * Signing in from a classroom headset: the students of the rooms the teacher
 * opened from this same public address today (see server/lib/classroomSignIn).
 * Everything is decided server-side; these only carry the organization and the
 * chosen student.
 */
const classroomClient = () => ({
  id: "classroom-sign-in",
  getActions: ($fetch: BetterFetch) => ({
    classroom: {
      students: (organizationSlug: string) =>
        $fetch<{ students: Array<{ id: string; name: string }> }>("/classroom/students", {
          query: { organizationSlug },
        }),
      signIn: (body: { organizationSlug: string; userId: string }) =>
        $fetch<{ user: { id: string; name: string } }>("/classroom/sign-in", { method: "POST", body }),
    },
  }),
});

export const authClient = createAuthClient({
  baseURL: process.env.APP_URL!,
  fetchOptions: {
    onRequest(context) {
      context.headers.set("Accept-Language", localStorage.getItem("lang") || "en");
    },
  },
  plugins: [
    organizationClient({ ac, roles, teams: { enabled: true }, schema: inferOrgAdditionalFields<typeof auth>() }),
    magicLinkClient(),
    anonymousClient(),
    adminClient(),
    classroomClient(),
  ],
});

const _useListOrganizations = authClient.useListOrganizations;

export const {
  admin,
  organization,
  signIn,
  signOut,
  signUp,
  useActiveMember,
  useActiveMemberRole,
  useActiveOrganization,
  useSession,
} = authClient;

const ORG_LIST_CHANGED_EVENT = "org-list-changed";

/**
 * Trigger a refetch of the organizations list in all mounted components.
 * Use this after admin API calls that create/delete organizations.
 */
export function invalidateOrgList() {
  window.dispatchEvent(new Event(ORG_LIST_CHANGED_EVENT));
}

/**
 * Wrapper around better-auth's useListOrganizations that also listens
 * for manual invalidation via invalidateOrgList().
 */
export function useListOrganizations() {
  const result = _useListOrganizations();
  const handler = () => result()?.refetch?.();
  window.addEventListener(ORG_LIST_CHANGED_EVENT, handler);
  onCleanup(() => window.removeEventListener(ORG_LIST_CHANGED_EVENT, handler));
  return result;
}
